// Pure (non-client) module so both the client-side context and the server
// component layout can share the cookie format. Bump the version any time
// the category set changes — stored consent of an older version is treated
// as "not yet decided" and the banner re-prompts.

export const CONSENT_COOKIE_NAME = "aov_cookie_consent"
export const CONSENT_COOKIE_VERSION = 1
export const CONSENT_COOKIE_MAX_AGE_DAYS = 365

export type ConsentCategories = {
  essential: true
  analytics: boolean
  marketing: boolean
}

export type PersistedConsent = {
  v: number
  analytics: boolean
  marketing: boolean
}

export const DEFAULT_REJECTED: ConsentCategories = {
  essential: true,
  analytics: false,
  marketing: false,
}

export const DEFAULT_ACCEPTED: ConsentCategories = {
  essential: true,
  analytics: true,
  marketing: true,
}

// Parses the raw cookie value (whether URL-encoded or not) into a
// ConsentCategories, or null if the cookie is absent / malformed / a
// different schema version.
export function parseConsentCookie(
  raw: string | undefined | null
): ConsentCategories | null {
  if (!raw) return null
  let json = raw
  try {
    const decoded = decodeURIComponent(raw)
    if (decoded.trimStart().startsWith("{")) json = decoded
  } catch {
    /* raw might already be plain JSON */
  }
  try {
    const parsed = JSON.parse(json) as PersistedConsent
    if (parsed.v !== CONSENT_COOKIE_VERSION) return null
    return {
      essential: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
    }
  } catch {
    return null
  }
}

// Builds the Set-Cookie value we write client-side.
export function serializeConsentCookie(value: ConsentCategories): string {
  const payload: PersistedConsent = {
    v: CONSENT_COOKIE_VERSION,
    analytics: value.analytics,
    marketing: value.marketing,
  }
  const maxAge = CONSENT_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60
  const encoded = encodeURIComponent(JSON.stringify(payload))
  return `${CONSENT_COOKIE_NAME}=${encoded}; max-age=${maxAge}; path=/; SameSite=Lax`
}
